import { useCallback, useEffect, useMemo } from 'react';
import { useDataColumns } from '@/widgets/PivotedView';
import { useDataFetcher, usePagedDataFetcher } from '@/shared/hooks';
import type { TotalData } from '@/shared/ui';
import type { DataResponse } from '../../model/types';

interface DataTableData {
  loading: boolean;
  total: TotalData | undefined;
  rows:
    | {
        [x: string]: string | number | boolean;
        id: number;
        isTotal: boolean;
      }[]
    | undefined;
  columns: string[];
  getNextPage: () => void;
}

interface Params {
  getRowsPage: (offset?: number) => Promise<DataResponse>;
  getTotal: () => Promise<TotalData>;
}

export const useData = ({ getRowsPage, getTotal }: Params): DataTableData => {
  const dataColumnsComparator = useCallback((a: string, b: string) => {
    return -a.localeCompare(b);
  }, []);
  const { dataColumns, addColumns, reset } = useDataColumns(
    dataColumnsComparator,
  );
  const { data: total, loading: totalLoading } = useDataFetcher(getTotal);
  const {
    data: rows,
    loading,
    getNextPage,
  } = usePagedDataFetcher<DataResponse['rows'][number]>({
    getPage: getRowsPage,
    responseProcessor: (response) => {
      const { rows, columns, hasNextPage } = response as DataResponse;

      addColumns(columns);

      return { data: rows as DataResponse['rows'][number][], hasNextPage };
    },
  });
  const preparedRows = useMemo(() => {
    if (!total && !rows) {
      return undefined;
    }

    return [
      ...(total
        ? [
            {
              id: -1,
              isTotal: true,
              ...total,
            },
          ]
        : []),
      ...(rows || []).map((row, i) => {
        return {
          id: i,
          isTotal: false,
          ...row,
        };
      }),
    ];
  }, [rows, total]);

  useEffect(() => {
    reset();
  }, [getRowsPage, reset]);
  useEffect(() => {
    addColumns(Object.keys(total ?? {}));
  }, [total, addColumns]);

  return {
    total,
    loading: totalLoading || loading,
    rows: preparedRows,
    columns: dataColumns,
    getNextPage,
  };
};
